/* @jsx h */
import { defineComponent, h, computed, PropType } from "vue";
import { getTodayInTimezone } from "@lemmonhq/orders";

export type CalendarProps = {
  selectedDate: Date;
  onDateSelect: (date: Date) => void;
  minDate?: Date;
  maxDate?: Date;
  compact?: boolean;
  timezone?: string;
};

export const Calendar = defineComponent<CalendarProps>({
  name: "Calendar",
  props: {
    selectedDate: {
      type: Date as PropType<Date>,
      required: true,
    },
    onDateSelect: {
      type: Function as PropType<(date: Date) => void>,
      required: true,
    },
    minDate: {
      type: Date as PropType<Date>,
      default: undefined,
    },
    maxDate: {
      type: Date as PropType<Date>,
      default: () => {
        const date = new Date();
        date.setMonth(date.getMonth() + 3);
        return date;
      },
    },
    compact: {
      type: Boolean,
      default: false,
    },
    timezone: {
      type: String,
      default: "UTC",
    },
  },
  setup(props) {
    // Get effective minDate using restaurant timezone
    const effectiveMinDate = computed(() => {
      if (props.minDate) return props.minDate;
      return getTodayInTimezone(props.timezone);
    });

    const currentMonth = computed(() => {
      return new Date(
        props.selectedDate.getFullYear(),
        props.selectedDate.getMonth(),
        1,
      );
    });

    const monthLabel = computed(() => {
      return currentMonth.value.toLocaleDateString("en-US", { month: "long" });
    });

    const yearLabel = computed(() => {
      return currentMonth.value.getFullYear();
    });

    const daysInMonth = computed(() => {
      const days: (Date | null)[] = [];
      const firstDay = new Date(currentMonth.value);
      const lastDay = new Date(
        currentMonth.value.getFullYear(),
        currentMonth.value.getMonth() + 1,
        0,
      );

      // Add empty slots for days before the first day of the month
      const startPadding = firstDay.getDay();
      for (let i = 0; i < startPadding; i++) {
        days.push(null);
      }

      // Add all days in the month
      for (let day = 1; day <= lastDay.getDate(); day++) {
        days.push(
          new Date(
            currentMonth.value.getFullYear(),
            currentMonth.value.getMonth(),
            day,
          ),
        );
      }

      return days;
    });

    const isToday = (date: Date | null) => {
      if (!date) return false;
      const today = getTodayInTimezone(props.timezone);
      return (
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear()
      );
    };

    const isSelected = (date: Date | null) => {
      if (!date) return false;
      return (
        date.getDate() === props.selectedDate.getDate() &&
        date.getMonth() === props.selectedDate.getMonth() &&
        date.getFullYear() === props.selectedDate.getFullYear()
      );
    };

    const isDisabled = (date: Date | null) => {
      if (!date) return true;
      // Compare dates without time component
      if (effectiveMinDate.value) {
        const minDateOnly = new Date(
          effectiveMinDate.value.getFullYear(),
          effectiveMinDate.value.getMonth(),
          effectiveMinDate.value.getDate(),
        );
        const dateOnly = new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
        );
        if (dateOnly < minDateOnly) return true;
      }
      if (props.maxDate) {
        const maxDateOnly = new Date(
          props.maxDate.getFullYear(),
          props.maxDate.getMonth(),
          props.maxDate.getDate(),
        );
        const dateOnly = new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
        );
        if (dateOnly > maxDateOnly) return true;
      }
      return false;
    };

    const navigateMonth = (offset: number) => {
      const newDate = new Date(currentMonth.value);
      newDate.setMonth(newDate.getMonth() + offset);
      props.onDateSelect(
        new Date(newDate.getFullYear(), newDate.getMonth(), 1),
      );
    };

    return () => (
      <div
        class={
          props.compact ? "" : "bg-white rounded-lg border border-gray-200 p-4"
        }
      >
        {/* Header */}
        <div class="flex items-center justify-between mb-3">
          <button
            type="button"
            onClick={() => navigateMonth(-1)}
            class="w-7 h-7 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors"
          >
            <span class="material-symbols-outlined text-gray-500 text-lg">
              chevron_left
            </span>
          </button>
          <div class="text-sm">
            <span class="font-medium text-gray-900">{monthLabel.value}</span>
            <span class="text-gray-500 ml-1 font-normal">{yearLabel.value}</span>
          </div>
          <button
            type="button"
            onClick={() => navigateMonth(1)}
            class="w-7 h-7 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors"
          >
            <span class="material-symbols-outlined text-gray-500 text-lg">
              chevron_right
            </span>
          </button>
        </div>

        {/* Day labels */}
        <div class="grid grid-cols-7 gap-1 mb-2">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
            <div
              key={day}
              class="text-center text-xs font-medium text-gray-500 py-2"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Days */}
        <div class="grid grid-cols-7 gap-1">
          {daysInMonth.value.map((date, index) => {
            if (!date) {
              return <div key={`empty-${index}`} class="aspect-square" />;
            }

            const disabled = isDisabled(date);
            const selected = isSelected(date);
            const today = isToday(date);

            return (
              <button
                key={date.toISOString()}
                type="button"
                onClick={() => !disabled && props.onDateSelect(date)}
                disabled={disabled}
                class={`
                  aspect-square rounded-lg flex items-center justify-center text-sm font-light transition-all
                  ${
                    disabled
                      ? "text-gray-300 cursor-not-allowed"
                      : "hover:bg-gray-100 cursor-pointer"
                  }
                  ${selected ? "bg-gray-900 text-white hover:bg-gray-800" : ""}
                  ${today && !selected ? "border border-gray-900" : ""}
                `}
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>
      </div>
    );
  },
});
